import { HttpErrorResponse } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { FacilitiesService, FacilityWriteContext } from '../../core/services/facilities.service';
import { IBed } from '../models/be/bed';
import { IRoom } from '../models/be/room';
import { IUnit } from '../models/be/unit';

export interface UnitStructureBed { clientId: string; id?: string; name: string; }
export interface UnitStructureRoom { clientId: string; id?: string; name: string; beds: UnitStructureBed[]; }
export interface UnitStructureDraft { name: string; rooms: UnitStructureRoom[]; }
export interface UnitStructureSnapshot { ward: IUnit; rooms: IRoom[]; }
export interface UnitStructureCreated { kind: 'unit' | 'room' | 'bed'; id: string; clientId?: string; roomClientId?: string; }

export class UnitStructureSaveError extends Error {
  constructor(message: string, public readonly reloadRequired = false) { super(message); }
}

/** Tracks acknowledged writes so a retry cannot recreate already saved rooms or beds. */
export class UnitStructureSaveSession {
  private readonly unit: IUnit;
  private readonly rooms: IRoom[];
  private busy = false;
  private uncertainCreation = false;
  public get reloadRequired(): boolean { return this.uncertainCreation; }
  constructor(private readonly api: FacilitiesService, initial: UnitStructureSnapshot) {
    this.unit = { ...initial.ward };
    this.rooms = initial.rooms.map(room => ({ ...room, beds: (room.beds || []).map(bed => ({ ...bed })) }));
  }
  private get context(): FacilityWriteContext {
    return { customerId: this.unit.customerId, organizationId: this.unit.organizationId };
  }
  private async write<T>(label: string, operation: Observable<T>, creation = false): Promise<T> {
    try { return await firstValueFrom(operation); }
    catch (error) {
      const uncertain = creation && (!(error instanceof HttpErrorResponse) || error.status === 0 || error.status >= 500);
      this.uncertainCreation ||= uncertain;
      const detail = error instanceof HttpErrorResponse
        ? (typeof error.error?.message === 'string' ? error.error.message : error.status === 403 ? 'Permesso negato.' : 'Richiesta non completata.')
        : error instanceof Error ? error.message : 'Richiesta non completata.';
      throw new UnitStructureSaveError(label + ': ' + detail, uncertain);
    }
  }
  private createdId(id: string | null | undefined): string {
    if (!id) {
      this.uncertainCreation = true;
      throw new UnitStructureSaveError('Il backend non ha restituito l’ID dell’elemento creato.', true);
    }
    return id;
  }
  private validate(draft: UnitStructureDraft, allowDelete: boolean): void {
    const validName = (name: string) => typeof name === 'string' && !!name.trim() && name.trim().length <= 255;
    if (!this.unit.buildingId || !this.unit.organizationId || !Number.isFinite(this.unit.customerId) || this.unit.customerId <= 0)
      throw new UnitStructureSaveError('Contesto cliente, organizzazione o edificio non disponibile. Riaprire la pagina.');
    if (!validName(draft.name) || !draft.rooms.length || draft.rooms.some(room => !validName(room.name) || room.beds.some(bed => !validName(bed.name))))
      throw new UnitStructureSaveError('Compilare il nome dell’unità, delle stanze e dei letti (massimo 255 caratteri).');
    if (new Set(draft.rooms.map(room => room.name.trim())).size !== draft.rooms.length
      || draft.rooms.some(room => new Set(room.beds.map(bed => bed.name.trim())).size !== room.beds.length))
      throw new UnitStructureSaveError('Ogni stanza deve avere un nome distinto nell’unità e ogni letto un nome distinto nella propria stanza.');
    const roomIds = draft.rooms.map(room => room.id).filter(Boolean);
    const bedIds = draft.rooms.flatMap(room => room.beds.map(bed => bed.id)).filter(Boolean);
    if (new Set(roomIds).size !== roomIds.length || new Set(bedIds).size !== bedIds.length)
      throw new UnitStructureSaveError('Identificativi duplicati nella struttura. Riaprire la pagina.');
    for (const room of draft.rooms) {
      const initial = this.rooms.find(item => item.id === room.id);
      if (room.id && !initial) throw new UnitStructureSaveError('Stanza non appartenente alla struttura caricata.');
      if (room.beds.some(bed => bed.id && !initial?.beds?.some(item => item.id === bed.id)))
        throw new UnitStructureSaveError('Letto non appartenente alla stanza caricata.');
    }
    if (!allowDelete && this.rooms.some(room => !roomIds.includes(room.id)
      || room.beds?.some(bed => !bedIds.includes(bed.id))))
      throw new UnitStructureSaveError('La rimozione di stanze e letti salvati è disponibile solo ai superuser.');
  }
  public async save(draft: UnitStructureDraft, allowDelete: boolean, created: (entity: UnitStructureCreated) => void): Promise<void> {
    if (this.busy) throw new UnitStructureSaveError('Salvataggio già in corso.');
    if (this.uncertainCreation) throw new UnitStructureSaveError('Riaprire la pagina per verificare l’esito della precedente creazione.', true);
    this.validate(draft, allowDelete);
    this.busy = true;
    try {
      const unitName = draft.name.trim();
      if (!this.unit.id) {
        const response = await this.write('Creazione unità', this.api.postUnit$({ ...this.context, buildingId: this.unit.buildingId, name: unitName }, this.context), true);
        this.unit.id = this.createdId(response.id); this.unit.name = unitName;
        created({ kind: 'unit', id: this.unit.id });
      } else if (unitName !== this.unit.name) {
        await this.write('Modifica unità', this.api.putUnit$({ ...this.context, id: this.unit.id, buildingId: this.unit.buildingId, name: unitName }, this.context));
        this.unit.name = unitName;
      }
      // A room deletion already cascades to its beds: do not issue duplicate bed deletes.
      for (const room of [...this.rooms]) {
        if (draft.rooms.some(item => item.id === room.id)) continue;
        const response = await this.write('Rimozione stanza «' + room.name + '»', this.api.deleteRoom$(room.id!, this.context));
        const remaining = await this.write('Verifica rimozione stanza', this.api.getRooms$(this.unit.id!, this.unit.organizationId));
        if (remaining.some(item => item.id === room.id))
          throw new UnitStructureSaveError('Stanza «' + room.name + '» non rimossa. ' + (response.results?.[room.id!] || 'Il backend la restituisce ancora.'));
        this.rooms.splice(this.rooms.indexOf(room), 1);
      }
      for (const desired of draft.rooms) {
        let room = this.rooms.find(item => item.id === desired.id);
        const name = desired.name.trim();
        if (!room) {
          const payload: IRoom = { ...this.context, name, unitId: this.unit.id!, status: 'Operational' };
          const response = await this.write('Creazione stanza «' + name + '»', this.api.postRoom$(payload, this.context), true);
          desired.id = this.createdId(response.id);
          room = { ...payload, id: desired.id, beds: [] }; this.rooms.push(room);
          created({ kind: 'room', id: desired.id, clientId: desired.clientId });
        } else if (room.name !== name) {
          await this.write('Modifica stanza «' + room.name + '»', this.api.putRoom$({ ...this.context, id: room.id, unitId: this.unit.id!, name, status: room.status }, this.context));
          room.name = name;
        }
        for (const bed of [...(room.beds || [])]) {
          if (desired.beds.some(item => item.id === bed.id)) continue;
          const response = await this.write('Rimozione letto «' + bed.name + '»', this.api.deleteBed$(bed.id!, this.context));
          const remaining = await this.write('Verifica rimozione letto', this.api.getBeds$(room.id!, this.context));
          if (remaining.some(item => item.id === bed.id))
            throw new UnitStructureSaveError('Letto «' + bed.name + '» non rimosso. ' + (response.results?.[bed.id!] || 'Il backend lo restituisce ancora.'));
          room.beds!.splice(room.beds!.indexOf(bed), 1);
        }
        for (const desiredBed of desired.beds) {
          const bed = room.beds?.find(item => item.id === desiredBed.id);
          const bedName = desiredBed.name.trim();
          if (!bed) {
            const payload: IBed = { ...this.context, name: bedName, roomId: room.id!, status: 'Unoccupied' };
            const response = await this.write('Creazione letto «' + bedName + '»', this.api.postBed$(payload, this.context), true);
            desiredBed.id = this.createdId(response.beds?.[0]?.id);
            (room.beds ||= []).push({ ...payload, id: desiredBed.id });
            created({ kind: 'bed', id: desiredBed.id, clientId: desiredBed.clientId, roomClientId: desired.clientId });
          } else if (bed.name !== bedName) {
            await this.write('Modifica letto «' + bed.name + '»', this.api.putBed$({ ...this.context, id: bed.id, roomId: room.id!, name: bedName, status: bed.status }, this.context));
            bed.name = bedName;
          }
        }
      }
    } finally { this.busy = false; }
  }
}
